export type MonsterRewardRounding = "round" | "ceil" | "floor";

export type MonsterRewardDefinition =
  | { kind: "fixed"; amount: number }
  | {
      kind: "level_formula";
      base: number;
      perLevel: number;
      multiplier: number;
      bonus?: number;
      rounding?: MonsterRewardRounding;
    };

export type MonsterExperienceRewardDefinition = MonsterRewardDefinition;
export type MonsterRyoRewardDefinition = MonsterRewardDefinition;

export function isMonsterRewardDefinition(
  value: unknown,
): value is MonsterRewardDefinition {
  if (!value || typeof value !== "object") return false;

  const reward = value as Record<string, unknown>;
  if (reward.kind === "fixed")
    return (
      typeof reward.amount === "number" &&
      Number.isInteger(reward.amount) &&
      reward.amount >= 0
    );

  if (reward.kind !== "level_formula") return false;
  const baseIsValid =
    typeof reward.base === "number" &&
    Number.isFinite(reward.base) &&
    reward.base >= 0;
  const perLevelIsValid =
    typeof reward.perLevel === "number" &&
    Number.isFinite(reward.perLevel) &&
    reward.perLevel >= 0;
  const multiplierIsValid =
    typeof reward.multiplier === "number" &&
    Number.isFinite(reward.multiplier) &&
    reward.multiplier > 0;
  const bonusIsValid =
    reward.bonus === undefined ||
    (typeof reward.bonus === "number" &&
      Number.isFinite(reward.bonus) &&
      reward.bonus >= 0);
  const roundingIsValid =
    reward.rounding === undefined ||
    reward.rounding === "round" ||
    reward.rounding === "ceil" ||
    reward.rounding === "floor";

  return (
    baseIsValid &&
    perLevelIsValid &&
    multiplierIsValid &&
    bonusIsValid &&
    roundingIsValid
  );
}

function roundReward(value: number, rounding: MonsterRewardRounding): number {
  switch (rounding) {
    case "ceil":
      return Math.ceil(value);
    case "floor":
      return Math.floor(value);
    case "round":
      return Math.round(value);
  }
}

export function calculateMonsterReward(
  level: number,
  reward: MonsterRewardDefinition,
): number {
  if (!Number.isInteger(level) || level < 1)
    throw new RangeError("monster level must be positive");
  if (!isMonsterRewardDefinition(reward))
    throw new RangeError("monster reward definition is invalid");
  if (reward.kind === "fixed") return reward.amount;

  const calculated = roundReward(
    (reward.base + reward.perLevel * level) * reward.multiplier +
      (reward.bonus ?? 0),
    reward.rounding ?? "round",
  );
  if (!Number.isFinite(calculated) || calculated < 0)
    throw new RangeError("calculated monster reward must be non-negative");
  return calculated;
}

export function calculateMonsterExperience(
  level: number,
  reward: MonsterExperienceRewardDefinition,
): number {
  return calculateMonsterReward(level, reward);
}

export function calculateMonsterRyo(
  level: number,
  reward: MonsterRyoRewardDefinition,
): number {
  return calculateMonsterReward(level, reward);
}
